import { JcBuiltInIcon, JcTitle } from "jcsdk";
import { JcCommonProps, JcUtils } from "../../utils/JcUtils";
import { JcRow, JcColumn } from "../JcBox";
import { JcButton, JcButtonType } from "../button/JcButton";
import { JcIconButton } from "../button/JcIconButton";
import { JcIconType } from "../icon/JcIcon";

export interface JcDialogProps extends JcCommonProps {
  readonly title?: React.ReactNode;
  readonly onClose?: () => void;
  readonly onOk?: () => void;
  readonly okButton?: React.ReactNode;
  readonly okDisabled?: boolean;
  /** Defaults to `PRIMARY`; pass `DELETE` for a destructive confirmation. */
  readonly okButtonType?: JcButtonType;
  readonly closeButton?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly hideCloseButton?: boolean;
  /** Suppresses the footer's text Close/Cancel button while keeping the top-bar close icon — for a panel whose only actions are picking an item or dismissing via the icon/backdrop/Escape, where a second, redundant "Close" button at the bottom adds nothing. */
  readonly hideCloseFooterButton?: boolean;
  /** Lets the user drag-resize the dialog from its corner (native CSS resize) — only worth it for content that can grow, e.g. a long list. */
  readonly resizable?: boolean;
  /** Optional node rendered at the start (left) of the footer, opposite Ok/Close —
   * e.g. a destructive "Delete" action. */
  readonly footerStart?: React.ReactNode;
}

export const JcDialog = (props: JcDialogProps): React.ReactElement<JcDialogProps> => {

  const commonProps = JcUtils.commonProps(props, [
    "jc-dialog",
    props.resizable && "jc-dialog-resizable"
  ]);

  const showTopBar = props.title != null || (props.onClose != null && !props.hideCloseButton)
  const showCloseFooterButton = props.onClose != null && !props.hideCloseFooterButton
  const showBottomBar = props.onOk != null || showCloseFooterButton || props.footerStart != null

  return <JcColumn {...commonProps}>
    {showTopBar
      && <JcRow alignCenter className="jc-dialog-top-bar">
        {props.title && <JcTitle title={props.title} />}
        {props.onClose && !props.hideCloseButton
          && <JcIconButton
            className="jc-dialog-close-icon"
            onClick={() => props.onClose && props.onClose()}
            iconProps={{ type: JcIconType.BUILT_IN, icon: JcBuiltInIcon.X_MARK }} />}
      </JcRow>}
    <JcColumn className="jc-dialog-content">
      {props.children}
    </JcColumn>
    {showBottomBar
      && <JcRow spacing className="jc-dialog-bottom-bar">
        {props.footerStart && <div className="jc-dialog-footer-start">{props.footerStart}</div>}
        {props.onOk
          && <JcButton
            disabled={props.okDisabled}
            buttonType={props.okButtonType ?? JcButtonType.PRIMARY}
            className="jc-dialog-ok"
            onClick={() => props.onOk && props.onOk()} >{props.okButton || "Ok"}
          </JcButton>}
        {showCloseFooterButton
          && <JcButton
            buttonType={JcButtonType.GHOST}
            className="jc-dialog-close"
            onClick={() => props.onClose && props.onClose()} >{props.closeButton || "Close"}
          </JcButton>}
      </JcRow>}
  </JcColumn>
}